import zindexes from '../../../zindexes';
import * as oldTheme from '../old';
import getChartTheme from './charts';
import colors from './colors';
import fonts from './fonts';
import getNotificationsTheme from './notifications';
import shadows from './shadows';
import sizes from './sizes';
import tabsTheme from './tabs';
const { radius, fixedSpacing, getAdaptiveSpacing } = sizes;

const FIXED_VALUES = {
    sideBar: {
        width: 240,
        padding: 16
    },
    input: {
        paddingTopBottom: 10,
        paddingLeftRight: 14,
        borderWidth: 1,
        trailingIcon: {
            paddingTopBottom: 10,
            paddingLeftRight: 14
        }
    },
    markRecord: {
        inputMaxWidth: 280  // Mobile constraint to prevent Records counter cutoff
    },
    datepicker: {
        navigationBorderWidth: 2,
        timepickerHourMinuteWidth: 55,
        dividerWidth: 1
    },
    // icon buttons will be 40px high
    utilityNav: { height: fixedSpacing.lg * 2 + 40 },
    modal: { maxWidth: 400 }
};
/**
 *
 * @param {import('../../styling').ThemeParameters} params
 * @returns {import('../../styling').BackboneTheme}
 */
export function themeValues(params) {
    const old = oldTheme.themeValues(params);
    // Adding in unused vars to exemplify the available parameters
    // remove when actually implementing the theme.

    const {
        darkMode = false,
        mobile = false,
        tablet = false,
        width,
        height,
        smallMobile,
        keyboardHeight = 0,
        isIos = false,
        ...themeOverrides
    } = params;

    const themeParams = {
        darkMode,
        mobile,
        tablet,
        width,
        height,
        smallMobile,
        keyboardHeight,
        isIos,
        hdMonitor: width >= 1920,
        laptop: width < 1920 && width >= 1440,
        themeOverrides
    };

    const mode = darkMode ? 'dark' : 'light';
    const density = mobile ? 'compact' : 'default';

    const spacing = getAdaptiveSpacing(params);
    // this is something that regularly needs to be accommodated for
    const buttonPadding = sizes.fixedSpacing.md;
    const charts = getChartTheme(themeParams);
    const notifications = getNotificationsTheme(themeParams);

    /** @type {import('../../styling').BackboneTheme} */
    let theme = {
        ...old,
        viewPadding: smallMobile ? 0 : sizes.fixedSpacing.sm,
        selectedTheme: 'gl2025',
        baseBackgroundColor: darkMode ? colors.coolGray900 : colors.coolGray100,
        iconSize: '20px', // todo gl2025: this is just needed for backwards compatibility, can be removed
        defaultFontColor: darkMode ? colors.coolGray50 : colors.coolGray700,
        disabledFontColor: darkMode ? colors.coolGray500 : colors.coolGray400,
        fonts: fonts.text,
        font: fonts.text.md.regular.fontFamily,
        fontAlternate: fonts.display.md.regular.fontFamily,
        fontSize: mobile ? sizes.font.md : sizes.font.sm, // 16px / 14px
        fontSizeSmall: sizes.font.xs, // 12px
        fontSizeLarge: sizes.font.md, // 16px
        // grid row height is 44px, header is 34px
        listLineHeight: 44,
        listHeaderHeight: 34,
        // List scrollbar overlay gradients
        listScrollbarOverlay: {
            vertical: darkMode
                ? 'linear-gradient(90deg, rgba(36, 43, 44, 0.00) 0%, rgba(36, 43, 44, 0.85) 90%)'
                : 'linear-gradient(90deg, rgba(255, 255, 255, 0.00) 0%, rgba(255, 255, 255, 0.85) 90%)',
            horizontal: darkMode
                ? 'linear-gradient(180deg, rgba(36, 43, 44, 0.00) 10%, #242B2C 150%)'
                : 'linear-gradient(180deg, rgba(255, 255, 255, 0.00) -10%, #FCFDFD 150%)'
        },
        textPadding: 0,
        buttonPadding,
        badge: {
            size: fixedSpacing.lg,
            error: darkMode ? colors.red600 : colors.red500,
            warning: darkMode ? colors.orange600 : colors.orange500,
            success: darkMode ? colors.green600 : colors.green500,
            info: colors.primary500
        },
        blocklyEditor: {
            menuEditorPadding: fixedSpacing.xs,
            toggleGroupMargin: fixedSpacing.sm,
            toggleSize: fixedSpacing['4xl'],
            accessModifier: {
                size: fonts.text.md.regular.fontSize,
                green: colors.green500,
                red: colors.red500,
                white: colors.white,
                margin: fixedSpacing.xs,
                noIconSize: fixedSpacing.xl
            }
        },
        card: {
            ...old.card,
            backgroundColor: 'unset',
            borderRadius: radius.xl,
            boxShadow: 'unset',
            header: {
                height: fixedSpacing['8xl'], // 80px
                textColor: darkMode ? colors.coolGray25 : colors.coolGray900,
                backgroundColor: 'transparent',
                font: mobile ? fonts.text.xl.semiBold : fonts.text.lg.semiBold,
                padding: `0 0 ${fixedSpacing.md}px 0`
            },
            body: {
                border: 'none',
                font: mobile ? fonts.text.lg.medium : fonts.text.sm.medium,
                // needed for RN:
                padding: '0',
                borderColor: 'transparent'
            },
            footer: {
                border: 'none',
                backgroundColor: 'inherit',
                padding: mobile ? '20px' : `${fixedSpacing['4xl'] - fixedSpacing.md}px 0 0 0`,
                borderColor: 'transparent'
            }
        },
        cardList: {
            padding: spacing.xl,
            backgroundColor: darkMode ? colors.coolGray900 : colors.neutralGray50,
            borderColor: darkMode ? colors.coolGray700 : colors.coolGray300
        },
        charts: {
            ...old.charts,
            ...charts
        },
        editableList: {
            margin: fixedSpacing.xl,
            header: {
                bottomPadding: fixedSpacing.md,
                bottomBorder: `solid 1px ${darkMode ? colors.coolGray500 : colors.coolGray300}`
            },
            row: {
                // special gradient colors for layered effect
                backgroundColor: darkMode ? 'rgba(0, 0, 0, 0.207)' : 'rgba(0, 50, 71, 0.12)',
                radius: radius['2xl'],
                padding: fixedSpacing.xl
            }
        },
        form: {
            ...old.form,
            entryMaxWidth: Math.min(350, width - 30),
            entryMinWidth: 200,
            entryMinHeight: mobile ? 68 : 86,
            entryMaxHeight: mobile ? 72 : 86,
            entryMaxHeightErrorOffset: 30,
            labelFont: mobile ? fonts.text.md.semiBold : fonts.text.sm.semiBold,
            textFont: mobile ? fonts.text.md.medium : fonts.text.sm.medium,
            buttonBar: {
                mobile: {
                    titleFont: fonts.text.lg.semiBold,
                    titleColor: darkMode ? colors.coolGray100 : colors.coolGray900,
                    assetNoFont: fonts.text.md.medium,
                    assetNoColor: darkMode ? colors.coolGray400 : colors.coolGray500
                },
                buttonSizeInPx: mobile ? 20 : 24,
                minHeight: fixedSpacing['4xl'], // 32px
                justifyContent: 'center',
                margin: old.native
                    ? 0
                    : smallMobile
                      ? `0 ${fixedSpacing.md}px`
                      : mobile
                        ? `${fixedSpacing.xl - buttonPadding}px ${fixedSpacing['3xl'] - buttonPadding}px`
                        : `${fixedSpacing.xl - buttonPadding}px ${fixedSpacing['5xl'] - buttonPadding}px`,
                boxShadow: darkMode ? shadows.dark.top : shadows.light.top
            },
            inactive: {
                backgroundColor: colors.coolGray600,
                bannerPadding: `${fixedSpacing.xl}px ${fixedSpacing['5xl']}px`,
                titleFont: fonts.text.md.semiBold,
                titleColor: colors.white,
                titleMarginBottom: fixedSpacing.xxs,
                textFont: fonts.text.xs.medium,
                textColor: colors.coolGray100,
                overlay: darkMode ? 'rgba(0, 0, 0, 0.5)' : 'rgba(255, 255, 255, 0.5)'
            }
        },
        layout: {
            summaryDetail: {
                summaryPadding: `${fixedSpacing['2xl']}px ${fixedSpacing['3xl']}px 0 ${fixedSpacing['3xl']}px`,
                detailPadding: `${fixedSpacing['2xl']}px 0 0 0`,
                backgroundColor: darkMode ? colors.coolGray800 : colors.white,
                minGridHeight: 125
            }
        },
        keySearch: {
            borderRadius: sizes.radius.full,
            borderColor: darkMode ? colors.coolGray500 : colors.coolGray400,
            iconColor: darkMode ? colors.coolGray300 : colors.coolGray600,
            padding: `${fixedSpacing.md}px ${fixedSpacing['xl']}px`,
            gap: fixedSpacing.md,
            input: {
                backgroundColor: darkMode ? colors.coolGray900 : colors.neutralGray50,
                fontWeight: fonts.text.sm.medium.fontWeight,
                color: darkMode ? colors.white : colors.coolGray900,
                lineHeight: fonts.text.sm.medium.lineHeight
            }
        },
        mainContainer: {
            margin: 0,
            height: height - FIXED_VALUES.utilityNav.height,
            padding: smallMobile ? 0 : mobile ? fixedSpacing['xl'] : fixedSpacing['3xl'],
            gap: fixedSpacing.xl,
            offset: 0
        },
        menu: {
            button: {
                withSelection: {
                    paddingLeft: fixedSpacing.xs,
                    borderColor: darkMode ? colors.coolGray700 : colors.coolGray300,
                    marginLeft: fixedSpacing.md,
                    color: darkMode ? colors.coolGray100 : colors.coolGray700
                }
            },
            borderColor: darkMode ? colors.coolGray300 : colors.coolGray600,
            borderWidth: 1,
            borderRadius: radius.md,
            boxShadow: darkMode ? shadows.dark.md : shadows.light.md,
            backgroundColor: darkMode ? colors.coolGray900 : colors.white,
            color: darkMode ? colors.coolGray100 : colors.coolGray700,
            padding: fixedSpacing.lg,
            gap: fixedSpacing.xs,
            menuItem: {
                padding: `${fixedSpacing.md}px ${fixedSpacing.lg}px`,
                hoverBackgroundColor: darkMode ? colors.coolGray700 : colors.coolGray100,
                defaultFont: fonts.text.sm.regular,
                iconTextGap: fixedSpacing.md,
                borderRadius: radius.xs
            },
            actionMenu: {
                offsetTop: fixedSpacing.md,
                padding: fixedSpacing.md,
                menuItem: {
                    defaultFont: fonts.text.sm.regular
                },
                divider: {
                    marginBottom: fixedSpacing.lg
                }
            },
            divider: {
                color: darkMode ? colors.coolGray700 : colors.coolGray300,
                margin: `${fixedSpacing.sm}px 0`
            },
            subMenu: {
                chevronSize: 16,
                chevronRightOffset: fixedSpacing.lg + fixedSpacing.md
            },
            header: {
                padding: `0 ${fixedSpacing.lg}px`,
                gap: fixedSpacing.xxs
            },
            footer: {
                color: darkMode ? colors.coolGray500 : colors.coolGray500
            },
            native: {
                marginTop: FIXED_VALUES.utilityNav.height,
                paddingTop: fixedSpacing.xl,
                paddingLeft: fixedSpacing.lg,
                shell: {
                    marginLeft: fixedSpacing.xl,
                    paddingLeft: fixedSpacing.lg,
                    paddingTop: fixedSpacing.md,
                    legacyPadding: 0
                },
                subMenu: {
                    marginLeft: fixedSpacing.xl,
                    paddingLeft: fixedSpacing.lg
                },
                divider: {
                    marginLeftRight: fixedSpacing.lg,
                    paddingTopBottom: fixedSpacing.sm
                },
                versionFooter: {
                    maxWidth: 250,
                    marginLeft: fixedSpacing['2xl'],
                    marginTop: fixedSpacing.xl
                }
            }
        },
        modal: {
            minWidth: mobile ? width : 300,
            /* For whatever reason, 90% does not yield the same value as width * 0.9 */
            maxWidth: mobile ? width - 2 * fixedSpacing.xl : width * 0.9,
            minHeight: mobile ? height : 0,
            backgroundColor: darkMode ? colors.coolGray800 : colors.white,
            // this matches the built-in background "fade" when a modal is open
            backgroundFade: 'rgba(0, 0, 0, 0.6)',
            borderRadius: radius.xl,
            padding: mobile
                ? `${fixedSpacing['2xl']}px ${fixedSpacing.xl - buttonPadding}px`
                : `${fixedSpacing['3xl'] - buttonPadding}px`,
            margin: mobile ? fixedSpacing.xl : fixedSpacing['4xl'], // 16px / 32px
            boxShadow: darkMode ? shadows.dark.md : shadows.light.md,
            header: {
                gap: fixedSpacing['2xl'],
                padding: mobile ? buttonPadding : `0 ${buttonPadding}px ${fixedSpacing.md}px ${buttonPadding}px`
            },
            body: {
                padding: mobile ? buttonPadding : `0 ${buttonPadding}px`
            },
            button: {
                minWidth: fixedSpacing['10xl'],
                maxWidth: 2 * fixedSpacing['11xl']
            },
            confirmation: {
                maxWidth: mobile ? width : FIXED_VALUES.modal.maxWidth
            },
            import: {
                maxWidth: width * 0.9 - 2 * fixedSpacing['4xl']
            },
            progressDialog: {
                // todo: gl2025 this is just used for backwards compatibility, to be removed
                // react native does when this is undefined
                padding: `0 ${buttonPadding}px ${fixedSpacing.md}px ${buttonPadding}px`,
                headerColor: 'transparent',
                margin: 0
            },
            columnCustomization: {
                padding: 0
            },
            native: {
                elevation: 10
            }
        },
        sideBar: {
            width: smallMobile ? 0.9 * width : mobile ? 0.75 * width : FIXED_VALUES.sideBar.width,
            widthIconOnly: mobile ? 0 : fixedSpacing['2xl'] + 2 * fixedSpacing['3xl'], // 68px
            backgroundColor: darkMode ? colors.coolGray800 : colors.white,
            borderColor: darkMode ? colors.coolGray700 : colors.coolGray200,
            borderWidth: 1,
            padding: fixedSpacing.xl, // 16px
            // Button size for drawer toggle button (24x24 icons)
            buttonSize: 'lg',
            floatingShadow: darkMode ? shadows.dark.right : shadows.light.right,
            // Footer:
            logoMaxHeight: fixedSpacing['5xl'] + 'px', // 40px - should be 32px, but our logos have white padding
            logoMaxWidth: FIXED_VALUES.sideBar.width - 2 * fixedSpacing.xl + 'px', // 208px
            taglineFont: mobile ? fonts.text.xs.medium : fonts.text.xs.medium,
            tagLineColor: darkMode ? colors.coolGray400 : colors.coolGray500,
            drag: {
                top: fixedSpacing['5xl'], // 40px
                radius: radius.xl,
                padding: `${fixedSpacing.xs}px ${fixedSpacing.xs}px ${fixedSpacing.xs}px ${fixedSpacing.xxs}px`,
                font: fonts.text.xl.medium
            }
        },
        titleBar: {
            marginLeft: FIXED_VALUES.sideBar.width,
            titleGap: 'auto',
            marginRight: fixedSpacing['3xl'], // 24px
            height: fixedSpacing['7xl'], // 64px
            hamburgerMinWidth: fixedSpacing['2xl'] + 2 * fixedSpacing['3xl'], // 68px
            hamburgerPadding: `${fixedSpacing.lg}px ${fixedSpacing.xl}px`, // 12px 16px
            // Padding 12x24
            paddingHorizontal: mobile ? 0 : fixedSpacing['3xl'], // 24px
            // Colors with dark mode support
            color: darkMode ? colors.white : colors.coolGray900,
            backgroundColor: darkMode ? colors.coolGray800 : colors.white,
            borderColor: darkMode ? colors.coolGray700 : colors.coolGray200,
            bottomBorderWidth: 1,
            // Typography
            font: mobile ? fonts.display.xs.semiBold : fonts.display.sm.semiBold,
            // Button size for header buttons (24x24 icons)
            buttonSize: 'lg',
            buttonMargin: fixedSpacing.sm // 6px
        },
        // Outer menus (sidebar, navigation, drawer menus)
        outerMenus: {
            backgroundColor: darkMode ? colors.coolGray800 : colors.white,
            border: darkMode ? colors.coolGray700 : colors.coolGray200,
            borderColor: darkMode ? colors.coolGray700 : colors.coolGray200,
            borderRadius: radius.sm,
            subMenuOffset: 0,
            borderWidth: 1,
            disabledBackgroundColor: darkMode ? colors.coolGray700 : colors.coolGray100,
            disabledFontColor: darkMode ? colors.coolGray400 : colors.coolGray500,
            font: mobile ? fonts.text.md.medium : fonts.text.sm.medium,
            fontColor: darkMode ? colors.coolGray50 : colors.coolGray700,
            gap: fixedSpacing.xs, // 4px
            minHeight: fixedSpacing['4xl'], // 32px
            padding: `${fixedSpacing.md}px ${fixedSpacing.xl}px`, // 8px 16px
            iconWidth: fixedSpacing['2xl'], // 20px
            textWidth: fixedSpacing['10xl'], // 128px
            iconOnly: {
                padding: `${fixedSpacing.md}px ${fixedSpacing.sm}px` // 8px 6px
            },
            active: {
                backgroundColor: colors.red500, // same for light and dark,
                backgroundColorHover: colors.red600, // same for light and dark
                fontColor: colors.white // same for light and dark
            },
            hover: {
                backgroundColor: darkMode ? colors.coolGray700 : colors.coolGray100,
                fontColor: darkMode ? colors.coolGray50 : colors.coolGray700
            },
            focus: {
                borderColor: colors.green500 // same for light and dark
            },
            disabled: {
                backgroundColor: darkMode ? colors.coolGray700 : colors.coolGray100,
                fontColor: darkMode ? colors.coolGray400 : colors.coolGray500
            },
            subMenu: {
                gap: fixedSpacing.none,
                paddingTop: fixedSpacing.sm, // 6px
                paddingBottom: fixedSpacing.md, // 8px
                marginBottom: fixedSpacing.lg, // 12px
                chevron: {
                    marginRight: 0,
                    width: fixedSpacing.lg, // 12px
                    height: fixedSpacing.lg // 12px
                }
            },
            subSubMenu: {
                backgroundColor: darkMode ? colors.coolGray900 : colors.coolGray50
            }
        },
        table: {
            shadow: darkMode ? '0px 4px 12px rgba(0, 0, 0, 0.4)' : `0px 4px 12px ${colors.coolGray300}`,
            rowBorderColor: darkMode ? colors.coolGray700 : colors.neutralGray300,
            rowHoverBackground: darkMode ? colors.primary900 : colors.primary50,
            rowHoverBorderColor: darkMode ? colors.primary600 : colors.primary400,
            pinnedDropShadow: darkMode ? '3px 0 10px 0 rgba(24, 29, 39, 0.5);' : '3px 0 10px 0 rgba(24, 29, 39, 0.10);',
            cellHorizontalPaddingInPx: sizes.fixedSpacing.lg,
            searchingTextContainer: {
                height: sizes.fixedSpacing['2xl'],
                margin: sizes.fixedSpacing.xs,
                bottom: 15,
                left: 10
            },
            offlineTextContainer: {
                left: 10
            },
            gridOuterContainer: {
                paddingBottom: sizes.fixedSpacing.md
            }
        },
        scrollbarTrack: darkMode ? colors.coolGray700 : colors.coolGray200,
        scrollbarThumb: darkMode ? colors.coolGray500 : colors.coolGray400,
        scrollbarThumbHover: darkMode ? colors.coolGray400 : colors.coolGray600,
        toast: {
            boxShadow: darkMode ? shadows.dark.md : shadows.light.md,
            color: colors.white,
            radius: radius.md,
            // padding includes 32px for space for the close button on the right
            padding: mobile
                ? `${fixedSpacing.lg}px ${fixedSpacing.xl + fixedSpacing['4xl']}px ${fixedSpacing.xl}px ${fixedSpacing.lg}px` // top:12px right:40px bottom:12px left:16px
                : `${fixedSpacing.xl}px ${fixedSpacing.xl + fixedSpacing['4xl']}px ${fixedSpacing.xl}px ${fixedSpacing.xl}px`, // top:16px right:40px bottom:16px left:16px
            margin: mobile ? fixedSpacing.xl : fixedSpacing['4xl'], // 16px / 32px
            // Somehow we can't update .Toastify__toast-container--bottom-left
            // so, we need to work with the standard 16px offset, and subtract that from the margins we want
            extraMargin: mobile ? 0 : fixedSpacing['4xl'] - 16, // 0px / 16px
            font: mobile ? fonts.text.md.regular : fonts.text.sm.regular,
            iconSpace: {
                width: mobile ? fixedSpacing['4xl'] : fixedSpacing['4xl'] + fixedSpacing.xl, // 32px / 32 + 16 = 48px
                height: mobile ? fixedSpacing['4xl'] + fixedSpacing.md : fixedSpacing['4xl'] // 32 + 8 = 49px  / 32px
            },
            buttonSize: fixedSpacing['2xl'], // 20px
            error: {
                backgroundColor: darkMode ? colors.red900 : colors.red600
            },
            warning: {
                backgroundColor: darkMode ? colors.orange900 : colors.orange600
            },
            success: {
                backgroundColor: darkMode ? colors.green900 : colors.green600
            },
            info: {
                backgroundColor: darkMode ? colors.primary900 : colors.primary600
            },
            // an expection to using an actual color. We're just brightening this a bit
            progressBarColor: 'rgb(255, 255, 255, 0.25)'
        },
        tooltip: {
            anchor: {
                margin: `0 ${sizes.fixedSpacing.sm}px ${sizes.fixedSpacing.sm}px ${sizes.fixedSpacing.sm}px`
            },
            titleFont: mobile ? fonts.text.sm.semiBold : fonts.text.xs.semiBold,
            bodyFont: mobile ? fonts.text.sm.regular : fonts.text.xs.regular,
            boxShadow: darkMode ? shadows.dark.md : shadows.light.md,
            padding: `${sizes.fixedSpacing.md}px ${sizes.fixedSpacing.lg}px`,
            radius: radius.md,
            backgroundColor: darkMode ? colors.coolGray900 : colors.white,
            color: darkMode ? colors.primary50 : colors.coolGray700,
            maxWidth: 2 * spacing['11xl'],
            zIndex: `${zindexes.InfoDisplayButtonMessage}`
        },
        userActivatedInput: {
            containerWidth: smallMobile ? '100%' : 300
        },
        input: {
            padding: `${FIXED_VALUES.input.paddingTopBottom}px ${FIXED_VALUES.input.paddingLeftRight}px`,
            gap: `${fixedSpacing.md}px`,
            borderRadius: radius.xs,
            borderWidth: `${FIXED_VALUES.input.borderWidth}px`,
            borderStyle: 'solid',
            borderColor: {
                default: darkMode ? colors.coolGray500 : colors.coolGray300,
                hover: darkMode ? colors.coolGray400 : colors.coolGray500,
                focus: colors.primary600,
                disabled: darkMode ? colors.coolGray600 : colors.coolGray200
            },
            borderColorDestructive: {
                default: darkMode ? colors.red600 : colors.red300,
                hover: darkMode ? colors.red400 : colors.red500,
                focus: darkMode ? colors.red400 : colors.red700
            },
            backgroundColor: {
                default: darkMode ? colors.coolGray800 : colors.white,
                disabled: darkMode ? colors.coolGray700 : colors.neutralGray25
            },
            fontColor: {
                default: darkMode ? colors.coolGray100 : colors.coolGray700,
                disabled: darkMode ? colors.coolGray500 : colors.coolGray400
            },
            font: fonts.text.sm.medium,
            placeholderFontColor: colors.coolGray500, // regardless of darkMode
            container: {
                gap: smallMobile ? 0 : mobile ? `${fixedSpacing.xs}px` : `${fixedSpacing.sm}px`
            },
            label: {
                font: fonts.text.xs.semiBold,
                color: {
                    default: darkMode ? colors.coolGray300 : colors.coolGray600,
                    disabled: darkMode ? colors.coolGray500 : colors.coolGray400
                }
            },
            hint: {
                font: fonts.text.xs.medium,
                color: {
                    default: darkMode ? colors.coolGray300 : colors.coolGray600,
                    destructive: darkMode ? colors.red400 : colors.red600,
                    disabled: darkMode ? colors.coolGray500 : colors.coolGray400
                }
            },
            leadingText: {
                paddingTopBottom: sizes.fixedSpacing.md,
                paddingLeftRight: sizes.fixedSpacing.lg,
                color: {
                    default: darkMode ? colors.coolGray300 : colors.coolGray700,
                    disabled: darkMode ? colors.coolGray500 : colors.coolGray400
                },
                font: fonts.text.md.regular
            },
            trailingIcon: {
                paddingTopBottom: FIXED_VALUES.input.trailingIcon.paddingTopBottom,
                paddingLeftRight: FIXED_VALUES.input.trailingIcon.paddingLeftRight,
                color: {
                    default: darkMode ? colors.coolGray300 : colors.coolGray700,
                    disabled: darkMode ? colors.coolGray500 : colors.coolGray400
                }
            }
        },
        markRecord: {
            inputMaxWidth: FIXED_VALUES.markRecord.inputMaxWidth
        },
        datepicker: {
            backgroundColor: darkMode ? colors.coolGray800 : colors.white,
            borderRadius: sizes.radius.xs,
            borderColor: darkMode ? colors.coolGray500 : colors.coolGray300,
            padding: sizes.fixedSpacing.md,
            header: {
                backgroundColor: darkMode ? colors.coolGray800 : colors.white,
                fontColor: darkMode ? colors.coolGray300 : colors.coolGray700,
                fontFamily: fonts.text.md.semiBold.fontFamily,
                fontSize: fonts.text.md.semiBold.fontSize,
                fontStyle: fonts.text.md.semiBold.fontStyle,
                fontWeight: fonts.text.md.semiBold.fontWeight,
                lineHeight: fonts.text.md.semiBold.lineHeight,
                marginTop: -sizes.fixedSpacing.xxs,
                marginBottom: sizes.fixedSpacing.md
            },
            dayName: {
                fontColor: darkMode ? colors.coolGray300 : colors.coolGray700,
                fontFamily: fonts.text.sm.medium.fontFamily,
                fontSize: fonts.text.sm.medium.fontSize,
                fontStyle: fonts.text.sm.medium.fontStyle,
                fontWeight: fonts.text.sm.medium.fontWeight,
                lineHeight: fonts.text.sm.medium.lineHeight,
                width: sizes.fixedSpacing['4xl'] + sizes.fixedSpacing.xxs,
                paddingTopBottom: sizes.fixedSpacing.md,
                paddingLeftRight: sizes.fixedSpacing.xs
            },
            day: {
                default: {
                    fontColor: darkMode ? colors.coolGray300 : colors.coolGray700,
                    fontWeight: fonts.text.sm.regular.fontWeight
                },
                hover: {
                    fontColor: colors.primary700,
                    backgroundColor: darkMode ? colors.primary300 : colors.primary50,
                    fontWeight: fonts.text.sm.medium.fontWeight
                },
                selected: {
                    fontColor: colors.white,
                    backgroundColor: colors.primary600,
                    selectedFontWeight: fonts.text.sm.medium.fontWeight
                },
                today: {
                    fontColor: darkMode ? colors.coolGray100 : colors.coolGray700,
                    backgroundColor: darkMode ? colors.coolGray700 : colors.coolGray100,
                    fontWeight: fonts.text.sm.medium.fontWeight
                },
                outsideMonth: {
                    fontColor: darkMode ? colors.coolGray400 : colors.coolGray500
                },
                fontFamily: fonts.text.sm.regular.fontFamily,
                fontSize: fonts.text.sm.regular.fontSize,
                fontStyle: fonts.text.sm.regular.fontStyle,
                lineHeight: fonts.text.sm.regular.lineHeight,
                width: sizes.fixedSpacing['4xl'] + sizes.fixedSpacing.xxs,
                paddingTopBottom: sizes.fixedSpacing.md,
                paddingLeftRight: sizes.fixedSpacing.xs,
                borderRadius: sizes.radius.full
            },
            navigation: {
                paddingTop: sizes.fixedSpacing['2xl'],
                paddingRight: sizes.fixedSpacing['3xl'],
                color: darkMode ? colors.coolGray400 : colors.coolGray500,
                borderWidth: FIXED_VALUES.datepicker.navigationBorderWidth
            },
            timepicker: {
                hourMinuteWidth: FIXED_VALUES.datepicker.timepickerHourMinuteWidth,
                colonFont: fonts.text.xl.bold,
                colonFontColor: darkMode ? colors.coolGray300 : colors.coolGray700,
                colonBottomPadding: sizes.fixedSpacing.sm,
                innerContainerGap: sizes.fixedSpacing.xs,
                outerContainerGap: sizes.fixedSpacing.lg,
                amButtonRadius: `${sizes.radius.xs}px 0 0 ${sizes.radius.xs}px`,
                pmButtonRadius: `0 ${sizes.radius.xs}px ${sizes.radius.xs}px 0`,
                amPmButtonMarginBottom: sizes.fixedSpacing.xxs,
                amPmButtonBoxShadow: `0 0 0 1px ${darkMode ? colors.coolGray700 : colors.coolGray300} inset`,
                amPmFontWeight: fonts.text.sm.medium.fontWeight,
                marginTop: sizes.fixedSpacing.xl,
                marginLeft: sizes.fixedSpacing.xs
            },
            monthTimeDivider: {
                width: FIXED_VALUES.datepicker.dividerWidth,
                color: darkMode ? colors.coolGray300 : colors.coolGray200,
                paddingBottom: sizes.fixedSpacing.md
            }
        },
        switch: {
            ...old.switch,
            track: {
                height: {
                    standard: {
                        sm: sizes.fixedSpacing['2xl'],
                        lg: sizes.fixedSpacing['3xl']
                    },
                    multistate: {
                        sm: sizes.fixedSpacing['2xl'],
                        lg: sizes.fixedSpacing['3xl']
                    }
                },
                width: {
                    standard: {
                        sm: sizes.fixedSpacing['4xl'] + sizes.fixedSpacing.xs, // 36px
                        lg: sizes.fixedSpacing['5xl'] + sizes.fixedSpacing.xs // 44 px
                    },
                    multistate: {
                        sm: sizes.fixedSpacing['6xl'] + sizes.fixedSpacing.xs, // 52px
                        lg: sizes.fixedSpacing['7xl'] // 64 px
                    }
                },
                color: {
                    unchecked: darkMode ? colors.coolGray500 : colors.coolGray300,
                    checked: colors.primary600,
                    intermediate: colors.coolGray400,
                    disabled: darkMode ? colors.coolGray700 : colors.coolGray100
                },
                borderRadius: radius.xl,
                border: {
                    width: 1,
                    color: darkMode ? colors.coolGray500 : colors.coolGray400
                },
                margin: {
                    sm: {
                        left: sizes.fixedSpacing.sm,
                        right: sizes.fixedSpacing.sm
                    },
                    lg: {
                        left: sizes.fixedSpacing.md,
                        right: sizes.fixedSpacing.md
                    }
                },
                padding: 5,
                boxShadow: `0 0 0 3px ${darkMode ? colors.neutralGray800 : colors.white}, 0 0 0 5px ${colors.green500}`
            },
            thumb: {
                height: {
                    sm: sizes.fixedSpacing.xl,
                    lg: sizes.fixedSpacing['2xl']
                },
                width: {
                    sm: sizes.fixedSpacing.xl,
                    lg: sizes.fixedSpacing['2xl']
                },
                color: {
                    // todo gl2025: don't differentiate between checked and unchecked
                    checked: colors.white,
                    unchecked: colors.white,
                    disabled: darkMode ? colors.coolGray600 : colors.coolGray50
                },
                position: {
                    sm: {
                        left: sizes.fixedSpacing.xxs,
                        bottom: 1
                    },
                    lg: {
                        left: sizes.fixedSpacing.xxs,
                        bottom: 1
                    }
                },
                transform: {
                    standard: {
                        sm: {
                            unchecked: 0,
                            checked: sizes.fixedSpacing.xl
                        },
                        lg: {
                            unchecked: 0,
                            checked: sizes.fixedSpacing['2xl']
                        }
                    },
                    multistate: {
                        sm: {
                            unchecked: 0,
                            checked: sizes.fixedSpacing['4xl'],
                            intermediate: sizes.fixedSpacing.xl
                        },
                        lg: {
                            unchecked: 0,
                            checked: sizes.fixedSpacing['5xl'],
                            intermediate: sizes.fixedSpacing['2xl']
                        }
                    }
                }
            },
            options: {
                color: {
                    selected: darkMode ? colors.coolGray400 : colors.coolGray700,
                    unselected: darkMode ? colors.coolGray500 : colors.coolGray500
                },
                font: {
                    sm: {
                        selected: {
                            ...fonts.text.sm.medium
                        },
                        unselected: {
                            ...fonts.text.sm.regular
                        }
                    },
                    lg: {
                        selected: {
                            ...fonts.text.md.medium
                        },
                        unselected: {
                            ...fonts.text.md.regular
                        }
                    }
                },
                gap: {
                    sm: sizes.fixedSpacing.xs,
                    lg: sizes.fixedSpacing.sm
                }
            }
        },
        dropdown: {
            row: {
                text: {
                    color: {
                        disabled: darkMode ? colors.coolGray600 : colors.coolGray300,
                        default: darkMode ? colors.coolGray100 : colors.coolGray700
                    },
                    font: {
                        ...fonts.text.sm.medium
                    }
                },
                padding: {
                    horizontal: sizes.fixedSpacing.lg,
                    vertical: sizes.fixedSpacing.md
                },
                backgroundColor: {
                    default: darkMode ? colors.coolGray900 : colors.white,
                    hover: darkMode ? colors.coolGray700 : colors.coolGray100
                },
                borderRadius: radius.xs,
                pixelsPerLineOfText: 17,
                border: {
                    width: 1,
                    style: 'solid',
                    color: darkMode ? colors.coolGray700 : colors.coolGray300
                }
            },
            menu: {
                padding: sizes.fixedSpacing.md,
                border: {
                    radius: sizes.radius.xs,
                    width: 1,
                    color: darkMode ? colors.coolGray700 : colors.coolGray300
                },
                backgroundColor: darkMode ? colors.coolGray900 : colors.white,
                boxShadow: darkMode ? '0 0 25px 0 rgba(0, 0, 0, 0.30)' : '0 0 20px 0 rgba(0, 0, 0, 0.09)'
            },
            mobile: {
                padding: `0 0 ${sizes.fixedSpacing.lg}px ${sizes.fixedSpacing.sm}px`
            }
        },
        rangeFieldContainer: {
            margin: 0,
            titleMargin: '8px',
            formField: {
                paddingBottom: sizes.fixedSpacing.md,
                marginBottom: 0
            }
        },
        searchPane: {
            borderRadius: sizes.radius.sm,
            rangeSearchElement: {
                margin: '16px 10px 16px 4px',
                borderRadius: sizes.fixedSpacing.lg
            },
            multiPickerDropdown: {
                marginTop: sizes.fixedSpacing.xl
            },
            formField: {
                marginTop: smallMobile ? sizes.fixedSpacing.xs : mobile ? sizes.fixedSpacing.xs : sizes.fixedSpacing.lg,
                marginRight: sizes.fixedSpacing.sm
            }
        },
        formField: {
            marginBottom: mobile ? sizes.fixedSpacing.xxs : sizes.fixedSpacing.md,
            marginTop: mobile ? sizes.fixedSpacing.xxs : sizes.fixedSpacing.md,
            padding: {
                top: 14,
                bottom: sizes.fixedSpacing.xs,
                left: sizes.fixedSpacing.xs,
                right: sizes.fixedSpacing.xs
            }
        },
        tabs: tabsTheme[mode][density],
        chartColors: charts.dataColors,
        notifications,
        ...themeOverrides
    };
    if (mobile) {
        theme = {
            ...theme,
            titleBar: {
                ...theme.titleBar,
                marginLeft: 68,
                marginRight: 0
            },
            dropdown: {
                ...theme.dropdown,
                row: {
                    ...theme.dropdown.row,
                    padding: {
                        horizontal: sizes.fixedSpacing['3xl'],
                        vertical: sizes.fixedSpacing.xl
                    }
                }
            }
        };
    }
    return theme;
}

export const displayName = '2025 Golden Lasso';
